import clsx from 'clsx'

import { useEffect, useState, useMemo } from 'preact/hooks'
import { formatMoney, globalState } from 'helpers/utils'
import { emitterProps, globalEmitter } from 'helpers/emitter-event'
import useCartGlobal, { CartConfigKeys } from 'hooks/useCartGlobal'
import useURL from 'hooks/useUrl'

import { createPortal } from 'preact/compat'
import PreactImage from 'snippets/image/image'
import Icon from 'snippets/icon/icon'
import { usePromolines } from '@/components/hooks/usePromolines'
import { Promoline } from 'libs/searchspring/components/Promoline'

const actionKeys = {
  PLUS: 'plus',
  MINUS: 'minus',
  REMOVE: 'remove'
}

const onChangeActionValue = (key, quantity) => {
  return {
    [actionKeys.PLUS]: quantity + 1,
    [actionKeys.MINUS]: quantity - 1,
    [actionKeys.REMOVE]: 0
  }[key]
}

const OnChangeTrigger = ({ type, value, className, p }) => {
  return (
    <div class={`group shrink-0 relative cursor-pointer ${p}`}>
      <input
        type="checkbox"
        data-action={type}
        class=" absolute inset-tl size-full cursor-pointer appearance-none border-0 bg-transparent opacity-0 z-2"
        aria-label={value}
      />
      <div class={className}>{value}</div>
    </div>
  )
}

/**
 *
 * Handles line item quantity update.
 *
 * @param {*} param0
 * @returns
 */
const MiniCartItemQuantity = ({ item, handleSetCartConfig }) => {
  const plusIcon = <Icon id="plus" />
  const minusIcon = <Icon id="minus" />
  const trashIcon = <span>Remove</span>
  const [error, setError] = useState(null)

  /** Checks if the current Line Item new quantity is above the available inventory  */
  const handleLineItemQuantityChange = (item, newQuantity) => {
    if (newQuantity > item.inventory_quantity) {
      setError('Maximum quantity reached')
      setTimeout(() => setError(null), 5000)
      return false // Return false instead of throwing to prevent crashes
    }
    setError(null)
    return true
  }

  const onLineItemQuantityChange = async (e) => {
    try {
      e.preventDefault()
      /** Get Action Key */
      const action = e.target.dataset.action
      /** Set MiniCart state to isLoading true */
      handleSetCartConfig(CartConfigKeys.isLoading, true)
      /** Get new line item quantity based off action */
      const newQuantity = onChangeActionValue(action, item.quantity)
      /** Check if the new quantity is valid */
      if (!handleLineItemQuantityChange(item, newQuantity)) {
        handleSetCartConfig(CartConfigKeys.isLoading, false)
        return // Exit early if quantity is invalid
      }
      /** Update line item quantity */
      await window.Cart.updateLineItemQuantity(
        {
          id: item.variant_id,
          quantity: newQuantity
        },
        { silent: false }
      )

      /** Poll Shopify for new Cart item and set cart config state */
      await window.Cart.getCart()
      handleSetCartConfig(CartConfigKeys.cart, window.Cart.value)
      handleSetCartConfig(CartConfigKeys.isLoading, false)
    } catch (error) {
      globalEmitter.emit(emitterProps.notice.global, {
        type: 'error',
        content: error.message
      })
      handleSetCartConfig(CartConfigKeys.isLoading, false)
    }
  }

  return (
    <>
      <form onChange={onLineItemQuantityChange} class="flex items-center justify-between gap-x-0.5 md:gap-x-1">
        <div class="shrink-0 flex border border-neutral-medium">
          <OnChangeTrigger
            p="btn btn-primary btn-transparent btn-square w-[36px] !border-0"
            type={actionKeys.MINUS}
            value={minusIcon}
            className={'size-fit children:w-[14px] children:h-[2px]'}
          />
          <div class="grow flex items-center justify-center text-s w-2 text-center">{item.quantity}</div>
          <OnChangeTrigger
            p='btn btn-primary btn-transparent btn-square w-[36px] !border-0'
            type={actionKeys.PLUS}
            value={plusIcon}
            className={'size-fit children:size-[14px]'}
          />
        </div>

        <OnChangeTrigger
          p="btn btn-light btn-light-alt btn-text btn-s link-line lined link-fade"
          type={actionKeys.REMOVE}
          value={trashIcon}
          className={''}
        />
      </form>
      <p class={clsx('hidden w-full paragraph-xs text-system-error transition-all', error && '!flex')}>{error}</p>
    </>
  )
}

/**
 *
 *
 * Mini Cart Snippet,
 * Currently hardcoded to handle the free shipping discount range.
 *
 * @param {*} param0
 * @returns
 */
function MiniCartSnippet({ cartSubTotal, freeShippingGoal }) {
  const handleFreeShippingValue = (amount) => {
    if (typeof amount === 'number' && amount > 0) {
      return amount
    }
    return parseInt(amount)
  }

  const freeShippingMinimum = handleFreeShippingValue(freeShippingGoal) ?? 10000
  const reachedGoal = freeShippingMinimum - cartSubTotal
  const message = reachedGoal <= 0 ? (
    <>
      <div class="shrink-0 flex w-[12px] h-[9px] children:size-full">
        <Icon id={'tick'} />
      </div>
      <p class="paragraph-s font-bold">You're receiving free shipping</p>
    </>
  ) : (
    <>
      <p class="paragraph-s">You're <span class="font-bold">{formatMoney(reachedGoal)} away</span> from free shipping</p>
    </>
  )
  const progressBarPercentage = Math.round((cartSubTotal / freeShippingGoal) * 100)
  const progressBarPercentageWidth = progressBarPercentage > 100 ? '100' : progressBarPercentage
  return (
    <div data-role="free_shipping_monitor" class="flex flex-col gap-0.375 px-margin pt-1.375">
      <div class="flex items-center gap-x-0.375">
        {message}
      </div>
      <div class="relative w-full h-[4px] bg-neutral-medium">
        <div
          class="absolute inset-tl w-shipping-bar-width h-full bg-brand-sage transition-all"
          style={{ '--shipping-bar-width': progressBarPercentageWidth + '%' }}
        ></div>
      </div>
    </div>
  )
}

/**
 *
 * Display line item price
 * Handles different price if discount is applied
 *
 * Props Cart Line Item
 *
 * @param {*} param0
 * @returns
 */
const ItemPrice = ({ item }) => {
  const SalePrice = ({ salePrice, originalPrice }) => {
    return (
      <div class="flex flex-col items-end text-right">
        <span class="paragraph-l text-brand-sale">{formatMoney(salePrice)}</span>
        <span class="paragraph-l line-through">{formatMoney(originalPrice)}</span>
      </div>
    )
  }

  // Priority order for non-B2B: Discounts -> Compare At -> Regular
  if (item.discounts.length > 0 && item.original_line_price !== item.line_price) {
    return <SalePrice salePrice={item.line_price} originalPrice={item.original_line_price} />
  } else if (item.compare_at_price > item.original_price) {
    return <SalePrice salePrice={item.line_price} originalPrice={item.compare_at_price * item.quantity} />
  }

  return (
    <div class="flex flex-col items-end text-right">
      <span class="paragraph-l">{formatMoney(item.original_line_price)}</span>
    </div>
  )
}

const MiniCartItemDiscount = ({ item }) => {
  return (
    <>
      {item.discounts && item.discounts.length > 0 && (
        <ul class="list-none">
          {item.discounts.map((discount) => {
            return (
              <li class="flex gap-x-0.25 items-center paragraph-xs text-neutral-secondary list-none">
                <div class="size-[14px] children:size-full">
                  <Icon id={'tag'} />
                </div>
                {discount.title}
              </li>
            )
          })}
        </ul>
      )}
    </>
  )
}

/**
 *
 * Displays line item image
 *
 * @param {*} imageSrc
 * @param {*} id
 * @param {*} productPath
 * @returns
 */
const MiniCartItemImage = ({ imageSrc, id, productPath }) => {
  return (
    <a href={productPath} class="shrink-0 link-fade" aria-label="Item image">
      <PreactImage className="flex w-[100px] md:w-[120px] aspect-1/1 children:size-full children:object-cover children:object-bottom" key={id} imageUrl={[imageSrc]} />
    </a>
  )
}

/**
 * Checks if item has universal sizing by looking for "Size" property
 * @param {Object} properties - Item properties object
 * @returns {boolean} - True if universal sizing exists
 */
const hasUniversalSizing = (properties) => {
  return properties && Object.prototype.hasOwnProperty.call(properties, 'Size')
}

/**
 * Formats size value based on gender and size array
 * @param {string} optionValue - The option value string
 * @param {string} gender - Product gender (Womens/Mens)
 * @returns {string} - Formatted size string or original value
 */
const formatSizeValue = (optionValue, gender) => {
  const itemSizesArray = optionValue.split(',')

  if (itemSizesArray.length <= 1) {
    return optionValue
  }

  let ukSizeValue = ''
  let usSizeValue = ''

  if (gender === 'Womens') {
    ukSizeValue = itemSizesArray[4] ? itemSizesArray[4].replace('WOMENS', '') : ''
    usSizeValue = itemSizesArray[5] ? itemSizesArray[5].replace('WOMENS', '') : ''
  } else if (gender === 'Mens') {
    ukSizeValue = itemSizesArray[6] ? itemSizesArray[6].replace('MENS', '') : ''
    usSizeValue = itemSizesArray[7] ? itemSizesArray[7].replace('MENS', '') : ''
  }

  const euSizeValue = itemSizesArray[3] ? itemSizesArray[3].replace('EURO', 'EU') : ''

  return `${usSizeValue} / ${ukSizeValue} / ${euSizeValue}`.trim()
}

/**
 *
 * Display the line item
 * 1) Title
 * 2) Variant Details (size, colour)
 * 3) Discount details (if applicable)
 * 4) Line Item price
 *
 * @param {*} param0
 * @returns
 */
const MiniCartItemDetails = ({ item }) => {
  const properties = item.properties
  const hasProperties = properties && Object.keys(properties).length > 0
  const productGender = item.product?.metafields?.utopia?.gender

  // Filter options to exclude size when universal sizing exists
  const filteredOptions = item.options_with_values.filter(option => {
    if (option.name === 'Size' && hasUniversalSizing(properties)) {
      return false
    }
    return true
  })

  return (
    <div class="flex flex-row justify-between gap-x-0.5 md:gap-x-1">
      <div class="flex flex-col">
        <h3 class="paragraph-l font-bold">
          {item.customer_facing_product_name || item.product_title}
        </h3>
        <div class="flex flex-col gap-y-0.375 mt-0.375">
          {(item.colour_name || filteredOptions.length > 0 || hasProperties) && (
            <span class="paragraph-b text-neutral-secondary">
              {[
                item.colour_name,
                ...filteredOptions.map(option =>
                  option.name === 'Size'
                    ? formatSizeValue(option.value, productGender)
                    : option.value
                ),
                ...(hasProperties && properties ? Object.keys(properties)
                  .filter(property => properties[property] && !property.startsWith('_'))
                  .map(property => properties[property]) : [])
              ].filter(Boolean).join(' | ')}
            </span>
          )}
          <MiniCartItemDiscount item={item} />
        </div>
      </div>
      <ItemPrice item={item} />
    </div>
  )
}

const MiniCartLineItem = ({ item, handleSetCartConfig, isLoading }) => {
  const loadingState = isLoading && 'opacity-50'

  const criteria = useMemo(() => ({
    tags: item.tags ?? [],
    collections: item.collections?.map(c => c.handle) ?? [],
    products: [item.handle]
  }), [item.tags, item.collections, item.handle])

  const allPromolines = usePromolines(criteria)

  const getObjects = (items) => {
    const exclusiveItem = items.find(item => item.exclusive)
    if (exclusiveItem) {
      return [exclusiveItem, null]
    }
    return [items[0] || null, items[1] || null]
  }

  const [firstPromoline, secondPromoline] = useMemo(() => getObjects(allPromolines), [allPromolines])

  return (
    <div class={`flex flex-col gap-y-0.75 md:gap-y-1 pb-1.5 border-b border-neutral-medium transition-all last:pb-0 last:border-b-0 ${loadingState}`}>
      <div class="flex flex-row gap-x-0.75 md:gap-x-1">
        <MiniCartItemImage imageSrc={item.featured_image.url} id={item.id} productPath={item.url} />
        <div class="flex-grow flex flex-col justify-between gap-y-1 size-full">
          <MiniCartItemDetails item={item} />
          <MiniCartItemQuantity item={item} handleSetCartConfig={handleSetCartConfig} />
          <MiniCartItemDiscount item={item} />
        </div>
      </div>
      {(firstPromoline || secondPromoline) && (
        <div class="flex flex-col gap-0.25">
          {firstPromoline && <Promoline promoline={firstPromoline} promolineSize="medium" />}
          {secondPromoline && <Promoline promoline={secondPromoline} promolineSize="medium" />}
        </div>
      )}
    </div>
  )
}

/**
 * Calculates total savings from compare_at_price discounts
 * @param {Array} cartItems - Array of cart items
 * @returns {number} - Total compare_at_price savings in cents
 */
const calculateCompareAtSavings = (cartItems) => {
  let compareAtSavings = 0

  for (const item of cartItems) {
    const linePricePerItem = item.line_price / item.quantity
    const compareAtPrice = item.compare_at_price

    if (compareAtPrice && compareAtPrice > linePricePerItem) {
      const savingsPerItem = compareAtPrice - linePricePerItem
      const itemSavings = savingsPerItem * item.quantity
      compareAtSavings += itemSavings
    }
  }

  return compareAtSavings
}

/**
 *
 * Displays cart total for Mini Cart.
 *
 * @param {*} param0
 * @returns
 */
const DisplayCartTotal = ({ total, totalCartDiscount, isLoading, cart, discountText, totalText }) => {
  const cartTotal = formatMoney(total)
  const compareAtSavings = calculateCompareAtSavings(cart.items)
  const totalDiscountAmount = totalCartDiscount + compareAtSavings
  const totalDiscount = formatMoney(totalDiscountAmount)
  const subtotal = formatMoney(total + totalDiscountAmount)
  const totalItems = cart.items.reduce((sum, item) => sum + item.quantity, 0)

  return (
    <div class="flex flex-col gap-y-1.5">
      <x-modal
        data-id="total-order-summary"
        class="dropdown-modal group flex flex-col container-full data-visible:pb-1.5 border-b border-neutral-light transition-all"
      >
        <button
          class="flex items-center justify-between gap-x-0.75 py-0.75 md:py-1.25 text-left link-fade"
          data-modal-parent="total-order-summary"
          data-modal-role="toggle"
          aria-label="Show order summary"
        >
          <span class="paragraph-s font-bold">Order Summary</span>
          <div class="flex items-center gap-x-0.5 paragraph-xs before:content-['Show '] group-data-visible:before:content-['Hide ']">
            <div class="w-[14px] h-[8px] group-data-visible:-rotate-180 transition-all children:size-full">
                <Icon id={'chevron-down'} />
            </div>
          </div>
        </button>
        <div class="dropdown-content">
          <div class="overflow-hidden">
            <div class={`flex flex-col gap-y-0.5 transition-all ${isLoading && 'opacity-50'}`}>
              <div class="flex justify-between gap-x-1">
                <p class="paragraph-s">{totalItems} {totalItems === 1 ? 'Item' : 'Items'}</p>
                <p class={`paragraph-s transition-all ${isLoading && 'opacity-50'}`}>{subtotal}</p>
              </div>
              <div class="flex justify-between gap-x-1">
                <p class="paragraph-s">{discountText || 'Discounts'}</p>
                <p class={`paragraph-s transition-all ${isLoading && 'opacity-50'}`}>-{totalDiscount}</p>
              </div>
            </div>
          </div>
        </div>
      </x-modal>
      <div class="flex justify-between gap-x-1">
        <p class="paragraph-m font-bold">{totalText || 'Total'}</p>
        <p class={`paragraph-m font-bold transition-all ${isLoading && 'opacity-50'}`}>{cartTotal}</p>
      </div>
    </div>
  )
}

/**
 *
 * Empty Cart state. Renders when the customers cart is empty
 *
 * @param {*} param0
 * @returns
 */
const EmptyCart = ({ isLoading, emptyDescription, emptyLink, emptyLinkText }) => {
  return (
    <>
      <div class={`grow flex flex-col gap-y-1.5 items-center justify-center px-margin py-6.25 text-center transition-all ${isLoading ? 'opacity-0' : ''}`}>
        <p class={'h5 font-medium'}>{emptyDescription}</p>
        {emptyLinkText &&
          <a href={emptyLink} className={'btn btn-primary btn-fill btn-m w-fit'} aria-label={emptyLinkText}>{emptyLinkText}</a>
        }
      </div>
    </>
  )
}

/**
 *
 * Cart Checkout button
 *
 * @param {*} param0
 * @returns
 */
const CheckoutButton = ({ checkoutButtonTitle }) => {
  return (
    <div>
      <a href="/checkout" class="btn btn-primary btn-fill btn-l w-full" aria-label={checkoutButtonTitle}>
        {checkoutButtonTitle}
      </a>
    </div>
  )
}

/**
 *
 * Displays the line items present in the the customers cart.
 *
 * @param {*} param0
 * @returns
 */
const CartLineItems = ({ cart, handleSetCartConfig, isLoading }) => {
  return (
    <div class="grow flex flex-col gap-y-1.5 overflow-y-scroll no-scrollbar overscroll-none px-margin py-1.5" data-role="cart_items">
      {cart.items.map((item) => {
        return <MiniCartLineItem item={{ ...item }} handleSetCartConfig={handleSetCartConfig} isLoading={isLoading} />
      })}
    </div>
  )
}

/**
 *
 * Mini Cart
 *
 * @returns
 */
export function MiniCart() {
  const path = useURL()
  const { cartConfig, handleSetCartConfig } = useCartGlobal()
  const globalCart = globalState.cart
  const { isLoading, cart } = cartConfig
  const freeShippingMinimum = globalCart?.freeShippingMinimum

  const { checkoutButtonTitle, emptyDescription, emptyLink, emptyLinkText, discountText, totalText } = globalCart

  useEffect(() => {
    const altPaymentButtons = document.querySelector('div.additional-checkout-buttons--vertical')
    if (!altPaymentButtons) return
    if (cart.items.length > 0) {
      altPaymentButtons.classList.remove('hidden')
    } else if (cart.items.length <= 0) {
      altPaymentButtons.classList.add('hidden')
    }
  }, [cartConfig])

  useEffect(() => {
    if (path.includes('timestamp=')) {
      handleSetCartConfig(CartConfigKeys.isLoading, true)
      window.Cart.getCart().then(() => {
        handleSetCartConfig(CartConfigKeys.cart, window.Cart.value)
        handleSetCartConfig(CartConfigKeys.isLoading, false)
      })
    }
  }, [path])

  return (
    <>
      {cart?.items?.length ? (
        <>
          <MiniCartPortal targetId="minicart-shipping">
            <MiniCartSnippet cartSubTotal={cart.items_subtotal_price} freeShippingGoal={freeShippingMinimum} />
          </MiniCartPortal>
          <MiniCartPortal targetId="minicart-items">
            <CartLineItems cart={cart} isLoading={isLoading} handleSetCartConfig={handleSetCartConfig} />
          </MiniCartPortal>
          <MiniCartPortal targetId="minicart-summary">
            <div className="flex flex-col gap-y-1">
              <DisplayCartTotal
                totalCartDiscount={cart.total_discount}
                total={cart.total_price}
                isLoading={isLoading}
                cart={cart}
                discountText={discountText}
                totalText={totalText}
              />
              <CheckoutButton checkoutButtonTitle={checkoutButtonTitle} />
            </div>
          </MiniCartPortal>
        </>
      ) : (
        <MiniCartPortal targetId="minicart-items">
          <EmptyCart cartConfig={cartConfig} globalCart={globalCart} isLoading={isLoading} emptyDescription={emptyDescription} emptyLink={emptyLink} emptyLinkText={emptyLinkText} />
        </MiniCartPortal>
      )}
    </>
  )
}

const MiniCartPortal = ({ children, targetId }) => {
  const target = document.getElementById(targetId)
  return target ? createPortal(children, target) : null
}
